import type { Removable } from "./removal"

/**
 * The workshop runs as an ordered list of rounds. Each round moves through its
 * own ordered list of phases. Both live on the session document so every
 * client sees the same position in the flow.
 *
 *   lobby      the room gathers; the facilitator tunes the rules
 *   pains      everyone writes what hurts, then the room votes
 *   job        the facilitator turns the winning pains into one job statement
 *   cards      the room picks the cards that fit the job; the facilitator curates
 *   priorities the facilitator sorts the shortlist into MoSCoW, live
 *   deep       the Must haves are taken apart, one at a time
 *   wrap       nothing more is collected; the room reads the result back
 *
 * Everything from `job` onwards belongs to a JOB, not to the session — the
 * facilitator can run the same stretch again for a second job.
 */
export const ROUND_ORDER = ["lobby", "pains", "job", "cards", "priorities", "deep", "wrap"] as const

export type Round = (typeof ROUND_ORDER)[number]

/**
 * Phases available per round, in order.
 *
 * The Pains round has four, not three: the tally stays hidden while the room
 * votes and the facilitator reveals it as a separate beat. `tally` is that beat.
 * Modelled as a phase rather than a `votesRevealed` flag because it is already
 * shared, already ordered, and needs no resetting — voting happens once per
 * session, before any job exists.
 *
 * The Cards round has two for the same reason the flow needs them separated:
 * `pick` is the room choosing, `review` is the facilitator curating what they
 * chose. They are different people acting, so they cannot be one phase.
 */
export const ROUND_PHASES = {
  lobby: ["setup"],
  pains: ["write", "reveal", "vote", "tally"],
  job: ["draft", "locked"],
  cards: ["pick", "review"],
  priorities: ["place"],
  deep: ["fill"],
  wrap: ["summary"],
} as const satisfies Record<Round, readonly string[]>

export type Phase = (typeof ROUND_PHASES)[Round][number]

/**
 * Where the session is in its own life, as opposed to where the room is in the
 * flow.
 *
 *   pending    created and shareable. Guests can join and say they are ready,
 *              but the flow has not started. This is what the lobby renders.
 *   active     the facilitator has started it; the round/phase pair is live.
 *   completed  finished by the facilitator. Read-only for everyone.
 *
 * There is no `expired` value: expiry is a moment passing, not a write someone
 * makes, so nothing would ever set it. It is derived from `expiresAt` — see
 * `utils/sessionLifecycle`.
 */
export type SessionStatus = "pending" | "active" | "completed"

/** How long a session stays joinable unless the facilitator extends it. */
export const DEFAULT_SESSION_LIFETIME_MS = 24 * 60 * 60 * 1000

/**
 * A full-screen interstitial the facilitator drives and participants wait on:
 * the curtain between rounds, and the two decisions about which pain point
 * becomes a job — after the vote, and after a job is finished.
 *
 * Modelled as its own field rather than as extra phases, because it can appear
 * over any round — folding it into `phase` would need a curtain variant of
 * every round.
 */
export type OverlayKind =
  /** Curtain before the Pains round. */
  | "ready"
  /** Curtain before the Cards round. */
  | "deck"
  /** Curtain before the Priorities round. */
  | "board"
  /** Curtain before the Deep dive round. */
  | "dive"
  /** Curtain before the wrap-up. */
  | "wrap"
  /** The vote is in: which pain point the first job runs on. */
  | "jobPick"
  /** A job is finished: run another one, or wrap up. */
  | "jobGate"

export interface SessionOverlay {
  kind: OverlayKind
  /**
   * For `jobPick` and `jobGate`, the pain the facilitator has highlighted (not
   * yet committed). Both ask the same question — which problem becomes a job —
   * so both park the answer here until the CTA opens one.
   */
  selection: string | null
}

export type TimerStatus = "idle" | "running" | "paused"

/**
 * Authoritative shared timer. Clients never keep their own countdown — they
 * derive remaining time from these fields, so every screen agrees.
 *
 * `startedAt` marks the beginning of the current running segment.
 * `elapsedMs` accumulates time from previous segments (i.e. before a pause).
 */
export interface TimerState {
  status: TimerStatus
  startedAt: number | null
  /** Total length of the timer, in milliseconds. */
  durationMs: number
  elapsedMs: number
}

export interface SessionConfig {
  /** Card family numbers in play. */
  families: number[]
  painsPerParticipant: number
  votesPerParticipant: number
  /** Max cards a participant may pick for one job. 0 means unlimited. */
  handCap: number
  /** Max cards the facilitator may file under Must have. */
  mustHaveCap: number
  /*
   * Which optional exercises the session runs. Each one drops a whole round out
   * of the flow, so `activeRounds` reads these — a disabled round is never
   * shown, never counted in progress, and can never be written as a position.
   *
   * The job, cards and priorities rounds are NOT optional: every card pick,
   * placement and deep-dive answer hangs off a job, so a session without one
   * has nothing to collect.
   */
  /** Collect and vote on pain points. Off means the facilitator brings the pain. */
  painsEnabled: boolean
  /**
   * Write the job as "As a … I want … so that …". Off means one plain sentence,
   * which is also all the lock gate then requires.
   */
  userStoryEnabled: boolean
  deepDiveEnabled: boolean
  /** Planned duration per round, in minutes. */
  roundDurations: Record<Round, number>
}

export interface Session extends Removable {
  id: string
  customerId: string
  /**
   * The customer's name as it stood when the session was created, denormalised
   * so the room can be told who the workshop is for without reading the customer
   * document — which is facilitator-only, and must stay that way.
   *
   * Refreshed by `syncCustomerToSessions` when the customer is renamed.
   */
  customerName: string
  /**
   * Every facilitator who may drive this session, copied from the customer.
   *
   * Denormalised deliberately. It is what the security rules test on every read
   * and write of the session and everything beneath it, and a rule that had to
   * fetch the customer instead would cost an extra document read per operation —
   * on the pain points a whole room is typing into at once. It also lets a guest,
   * who may not read customers at all, still learn that they are NOT the
   * facilitator.
   *
   * Kept in step by `syncCustomerToSessions`, which fans a membership change out
   * over the customer's sessions.
   */
  facilitatorIds: string[]
  name: string
  status: SessionStatus
  round: Round
  phase: Phase
  /** The job currently being worked, or null before the first one is created. */
  currentJobId: string | null
  /** Shared interstitial, or null when the room is in a normal round. */
  overlay: SessionOverlay | null
  /*
   * Pain points the facilitator loaded before the room ever writes one — what
   * the client already told them. Configuration, not session data: they are
   * copied into the pains collection when the Pains round opens, because a pain
   * has to have an id before anyone can vote for it.
   */
  knownPains: string[]
  config: SessionConfig
  timer: TimerState
  /** When the facilitator started the flow. Null while `pending`. */
  startedAt: number | null
  /** When the facilitator finished it. Null unless `completed`. */
  completedAt: number | null
  /**
   * When the facilitator pressed Save & quit to park the session for a follow-up
   * call, or null when it is running normally. The session stays `active` at its
   * current round — this only marks it "paused" for the lists (see `sessionState`)
   * and is cleared the moment the room moves again (`writePosition`, `startTimer`).
   */
  pausedAt: number | null
  /**
   * When the share link stops working, as epoch ms.
   *
   * A plain number, not a `serverTimestamp()`: this is a point in the FUTURE, so
   * there is no server value to ask for, and a server stamp would read back null
   * until acknowledged — leaving a session that briefly looks like it never
   * expires. Computed on the client at creation, which costs at most that
   * browser's clock skew against a 24-hour window.
   */
  expiresAt: number | null
  createdAt: number | null
  updatedAt: number | null
}

/**
 * The facilitator's own notes, one entry per round.
 *
 * NOT a field on the session. The session document is world-readable — its id is
 * the share link — and Firestore hands over whole documents, so a note kept
 * there would be one devtools tab away from the room it is written about. It
 * lives at `sessions/{id}/private/notes`, which only the session's facilitators
 * may read.
 */
export type SessionNotes = Partial<Record<Round, string>>

export type SessionInput = Partial<
  Omit<Session, "id" | "createdAt" | "updatedAt" | "config" | "timer" | keyof Removable>
> & {
  config?: Partial<SessionConfig>
  timer?: Partial<TimerState>
}

/*
 * Defaults, reconciled against the prototype's own `DEFAULTS`:
 *
 *   painsEach 3 · votes 2 · handCap 5 · proveCap 3 · deepDive true ·
 *   userStory true · families 1-4, 6, 7 · mins {5,10,8,15,10,15,5}
 *
 * Every exercise is ON out of the box, the deep dive included — a new session
 * plans the full 68 minutes, which is what "Full discovery" also selects. The
 * deep dive used to default off here, which quietly made a fresh session a
 * six-round, 53-minute one and meant no template matched it.
 *
 * Family 5 (Monitors) is omitted: it is retired from the deck, so a new session
 * must not deal it. It stays resolvable for sessions that already played it.
 */
export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  families: [1, 2, 3, 4, 6, 7],
  painsPerParticipant: 3,
  votesPerParticipant: 2,
  handCap: 5,
  mustHaveCap: 3,
  painsEnabled: true,
  userStoryEnabled: true,
  deepDiveEnabled: true,
  roundDurations: {
    lobby: 5,
    pains: 10,
    job: 8,
    cards: 15,
    priorities: 10,
    deep: 15,
    wrap: 5,
  },
}

export const INITIAL_TIMER_STATE: TimerState = {
  status: "idle",
  startedAt: null,
  durationMs: 0,
  elapsedMs: 0,
}
